import React, {useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../incase-domain/components/button/Button';
import DropdownMenuCompact from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Label from '../../../../incase-domain/components/label/Label';
import {IElement} from '../../../../incase-domain/elements';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {FLIP_CARD_SIDE, IElementFlipCard} from '../../../../incase-domain/elements/flipCard';
import {getEmptyElement} from '../../../../service/element';
import HighlightedInputMedia from '../../../fileLoader/input.media';
import useImgFileReader from '../../../fileLoader/util/useImgFileReader';
import FlipCardElement from './flipCardElement';

interface FlipCardElementSelectProps {
    errors: IValidationErrorObject | null;
    flipCardSide: FLIP_CARD_SIDE;
    highlightedElement: IElementFlipCard;
    highlightedElementChangeHandler: {single: (field: keyof IElementFlipCard | string, value: unknown) => void};
}

export default function FlipCardElementSelect(props: FlipCardElementSelectProps): JSX.Element {
    const {errors, flipCardSide, highlightedElement, highlightedElementChangeHandler} = props;
    const {t} = useTranslation();

    const mediaInputRef = useRef<HTMLInputElement>(null);

    const flipCardTypeOptions = useMemo(
        () => [
            {
                label: t('flipCard.flipCardType.image'),
                value: ELEMENT.IMAGE,
            },
            {
                label: t('flipCard.flipCardType.text'),
                value: ELEMENT.STATIC_TEXT,
            },
            {
                label: t('flipCard.flipCardType.html'),
                value: ELEMENT.HTML,
            },
        ],
        [t]
    );

    const imgOnLoad = useImgFileReader((images) => {
        const {imageSrc, widthHeightRatio} = images[0];

        setSideElement({
            ...getEmptyElement(highlightedElement.slideId, ELEMENT.IMAGE, {imageSrc, widthHeightRatio}),
            name: flipCardSide,
        });
    });

    function setSideElement(element: IElement): void {
        highlightedElementChangeHandler.single('flipCardElement', {
            ...highlightedElement.flipCardElement,
            [flipCardSide]: {
                ...element,
                name: flipCardSide,
            },
        });
    }

    function onClick(type: ELEMENT): void {
        if (type === ELEMENT.IMAGE) {
            mediaInputRef.current?.click();
            return;
        }

        setSideElement({
            ...getEmptyElement(highlightedElement.slideId, type),
            name: flipCardSide,
        });
    }

    const flipCardTypeItems = [
        flipCardTypeOptions.map((el) => ({
            label: el.label,
            onClick: () => onClick(el.value),
        })),
    ];

    const flipCardElementSelectedType = useMemo(() => {
        if (highlightedElement.flipCardElement && highlightedElement.flipCardElement[flipCardSide]) {
            return flipCardTypeOptions.find((option) => option.value === highlightedElement.flipCardElement[flipCardSide].type);
        }
        return undefined;
    }, [flipCardSide, flipCardTypeOptions, highlightedElement.flipCardElement]);

    return (
        <>
            <div className="l-group--form u-mt-1">
                <Label className="u-mb-1">{t('flipCard.flipCardType.type')}</Label>
                <DropdownMenuCompact width="full" items={flipCardTypeItems}>
                    <Button
                        fullWidth
                        size="sm"
                        variant="soft"
                        trailing="solar:alt-arrow-down-linear"
                        className="c-sidepanel__dropdown_button"
                    >
                        {flipCardElementSelectedType?.label || t('flipCard.chooseType')}
                    </Button>
                </DropdownMenuCompact>
            </div>
            <div style={{marginTop: '1rem'}}>
                <FlipCardElement
                    selectedType={flipCardElementSelectedType?.value}
                    flipCardSide={flipCardSide}
                    errors={errors}
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={highlightedElementChangeHandler}
                />
            </div>

            <div className="u-d-none">
                <HighlightedInputMedia inputRef={mediaInputRef} onLoad={imgOnLoad} type="image" />
            </div>
        </>
    );
}
